import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { MasterFilters, MasterSummaryCards, MasterStatsRow, MasterToggle } from '@/pages/masters/common/commonComponents';
import {
    createWorkCenter,
    getAllWorkCenters,
    updateWorkCenter,
    deleteWorkCenter,
    getNextWorkCenterCode,
    getWorkCenterTypes,
    type WorkCenterResponse,
    type WorkCenterTypeDef,
} from '@/api/workcenter.api';
import { getAllCostCenters, type CostCenterResponse } from '@/api/costcenter.api';
import { getAllShifts, type ShiftResponse } from '@/api/shift.api';
import { getAllMachines, type MachineResponse } from '@/api/machine.api';
import { getAllOperators, type OperatorResponse } from '@/api/operators.api';
import SearchableMultiSelect, { type MultiSelectOption } from '@/components/base/SearchableMultiSelect';
import { invalidateWorkCenters } from '@/hooks/useWorkCenters';
import { getAllWarehouses } from '@/api/warehouse.api';
import { useWarehouseStore } from '@/stores/warehouseStore';

type WCType = 'MACHINE' | 'LABOR' | 'BOTH' | string;

interface WorkCenter {
    id: string;
    code: string;
    name: string;
    type: WCType;
    warehouseId: string | null;
    description: string;
    isActive: boolean;
    costCenterId: string | null;
    costCenterName: string | null;
    workingHoursPerDay: number;
    efficiencyPct: number;
    shiftIds: string[];
    shiftNames: string[];
    machineIds: string[];
    machineNames: string[];
    personsRequired: number | null;
    stdLabourRate: number | null;
    operatorWageBasis: 'TOTAL' | 'AVG';
    stdCost: { labour: number; operators: number; machines: number; total: number };
}

interface WCForm {
    code: string;
    name: string;
    type: WCType;
    warehouseId: string;
    description: string;
    isActive: boolean;
    costCenterId: string;
    workingHoursPerDay: string;
    efficiencyPct: string;
    shiftIds: string[];
    machineIds: string[];
    personsRequired: string;
    stdLabourRate: string;
    operatorWageBasis: 'TOTAL' | 'AVG';
}

interface TypeOption {
    value: string;
    label: string;
    machineRequired: boolean;
    isSystem: boolean;
}

// Cost centers a work center can belong to (department grouping)
const WORK_CENTER_COST_CENTER_TYPES = ['PRODUCTION', 'QUALITY', 'MAINTENANCE'];

/** Working hours of a shift: end − start (past midnight wraps) − break. */
function shiftHours(s: { start_time: string; end_time: string; break_minutes: number }) {
    const toMin = (t: string) => {
        const [h, m] = String(t || '0:0').split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };
    let span = toMin(s.end_time) - toMin(s.start_time);
    if (span <= 0) span += 24 * 60;
    return Math.max(0, (span - (Number(s.break_minutes) || 0)) / 60);
}

const fmtHours = (h: number) => String(Math.round(h * 100) / 100);

/** The server's message for a failed request (e.g. "Work Center with this name already exists"), else the error's own. */
const apiErrorMessage = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    || (err instanceof Error ? err.message : fallback);

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** 'monday' / 'MON' / 'Mon' → 'Mon' */
const dayKey = (d: string) => String(d || '').slice(0, 3).charAt(0).toUpperCase() + String(d || '').slice(1, 3).toLowerCase();

/** Shift as minutes from midnight; an end at or before the start runs past midnight. */
function shiftSpan(s: { start_time: string; end_time: string }) {
    const toMin = (t: string) => {
        const [h, m] = String(t || '0:0').split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };
    const start = toMin(s.start_time);
    let end = toMin(s.end_time);
    if (end <= start) end += 24 * 60;
    return { start, end, overnight: end > 24 * 60 };
}

/** Two shifts overlap when they share a working day and their times cross (past midnight included). */
function shiftsOverlap(a: ShiftResponse, b: ShiftResponse) {
    const daysA = new Set((a.working_days || []).map(dayKey));
    if (!(b.working_days || []).some((d) => daysA.has(dayKey(d)))) return false;
    const x = shiftSpan(a);
    const y = shiftSpan(b);
    return [0, 24 * 60, -24 * 60].some((shift) => x.start < y.end + shift && y.start + shift < x.end);
}

const MACHINE_STATUS_BADGE: Record<string, string> = {
    RUNNING: 'bg-green-50 text-green-700 border-green-200',
    IDLE: 'bg-slate-50 text-slate-600 border-slate-200',
    MAINTENANCE: 'bg-amber-50 text-amber-700 border-amber-200',
    BREAKDOWN: 'bg-red-50 text-red-700 border-red-200',
};

const fmtDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/** Next preventive maintenance: last + frequency; null when either is missing. */
function nextMaintenance(m: MachineResponse): { date: Date; overdue: boolean } | null {
    if (!m.last_maintenance_date || !m.maintenance_frequency_days) return null;
    const date = new Date(m.last_maintenance_date);
    date.setDate(date.getDate() + Number(m.maintenance_frequency_days));
    return { date, overdue: date.getTime() < Date.now() };
}

const DEFAULT_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: 'MACHINE', label: 'Machine — uses machinery only' },
    { value: 'LABOR', label: 'Labor — manual work only' },
    { value: 'BOTH', label: 'Both — machine + labor combined' },
];

const DEFAULT_TYPE_BADGES: Record<string, { label: string; cls: string }> = {
    MACHINE: { label: 'Machine', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    LABOR: { label: 'Labor', cls: 'bg-green-50 text-green-700 border-green-200' },
    BOTH: { label: 'Both', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
};

const DYNAMIC_PALETTES = [
    'bg-amber-50 text-amber-700 border-amber-200',
    'bg-emerald-50 text-emerald-700 border-emerald-200',
    'bg-violet-50 text-violet-700 border-violet-200',
    'bg-sky-50 text-sky-700 border-sky-200',
    'bg-rose-50 text-rose-700 border-rose-200',
    'bg-indigo-50 text-indigo-700 border-indigo-200',
    'bg-teal-50 text-teal-700 border-teal-200',
    'bg-orange-50 text-orange-700 border-orange-200',
];

export function getTypeBadge(type: string): { label: string; cls: string } {
    if (!type) return { label: '—', cls: 'bg-slate-50 text-slate-600 border-slate-200' };
    const upper = type.toUpperCase();
    if (DEFAULT_TYPE_BADGES[upper]) {
        return DEFAULT_TYPE_BADGES[upper];
    }
    let hash = 0;
    for (let i = 0; i < type.length; i++) {
        hash = (hash << 5) - hash + type.charCodeAt(i);
        hash |= 0;
    }
    const idx = Math.abs(hash) % DYNAMIC_PALETTES.length;
    return {
        label: type,
        cls: DYNAMIC_PALETTES[idx],
    };
}

// Letters, digits, - _ / — same rule as the server
const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_/-]{0,19}$/;

const emptyForm: WCForm = {
    code: '', name: '', type: 'MACHINE', warehouseId: '', description: '', isActive: true,
    costCenterId: '', workingHoursPerDay: '8', efficiencyPct: '100', shiftIds: [], machineIds: [],
    personsRequired: '', stdLabourRate: '', operatorWageBasis: 'TOTAL',
};

// ─── Slide-Over Form ──────────────────────────────────────────────────────────
interface SlideOverProps {
    open: boolean;
    editing: WorkCenter | null;
    onClose: () => void;
    onSave: (form: WCForm) => Promise<void>;
    warehouses: { id: string; name: string; is_active: boolean }[];
    /** Warehouse selected in the header: a new work center starts in it */
    defaultWarehouseId?: string | null;
    typeOptions: TypeOption[];
    costCenters: CostCenterResponse[];
}

function WorkCenterSlideOver({
    open,
    editing,
    onClose,
    onSave,
    warehouses,
    defaultWarehouseId,
    typeOptions,
    costCenters,
}: SlideOverProps) {
    const [form, setForm] = useState<WCForm>({ ...emptyForm });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    //const [isAddingType, setIsAddingType] = useState(false);
    const [newTypeInput, setNewTypeInput] = useState('');
    const [newTypeNeedsMachine, setNewTypeNeedsMachine] = useState(false);
    const firstInputRef = useRef<HTMLInputElement>(null);

    // Shifts and machines of the warehouse chosen in the form, plus the ones with no warehouse set
    // (usable anywhere); all of them while no warehouse is chosen
    const [shifts, setShifts] = useState<ShiftResponse[]>([]);
    const [machines, setMachines] = useState<MachineResponse[]>([]);
    const [loadingResources, setLoadingResources] = useState(false);
    // Set when the user picks another warehouse: picks that belong to the old one are dropped after the reload
    const pruneOnLoadRef = useRef(false);
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const warehouseId = form.warehouseId || undefined;
        setLoadingResources(true);
        const shiftsOf = (wh?: string) => getAllShifts(wh).then((r) => r.data ?? []).catch(() => [] as ShiftResponse[]);
        const machinesOf = (wh?: string) => getAllMachines(wh).then((r) => r.data ?? []).catch(() => [] as MachineResponse[]);
        const noWarehouse = <T extends { warehouse_id?: string | null }>(rows: T[]) => rows.filter((r) => !r.warehouse_id);
        Promise.all([
            shiftsOf(warehouseId),
            machinesOf(warehouseId),
            warehouseId ? shiftsOf().then(noWarehouse) : Promise.resolve([] as ShiftResponse[]),
            warehouseId ? machinesOf().then(noWarehouse) : Promise.resolve([] as MachineResponse[]),
        ]).then(([whShifts, whMachines, sharedShifts, sharedMachines]) => {
            if (cancelled) return;
            const shiftRows = [...whShifts, ...sharedShifts];
            const machineRows = [...whMachines, ...sharedMachines];
            setShifts(shiftRows);
            setMachines(machineRows);
            setLoadingResources(false);
            if (pruneOnLoadRef.current) {
                pruneOnLoadRef.current = false;
                const shiftIds = new Set(shiftRows.map((r) => r.id));
                const machineIds = new Set(machineRows.map((r) => r.id));
                setForm((p) => ({
                    ...p,
                    shiftIds: p.shiftIds.filter((id) => shiftIds.has(id)),
                    machineIds: p.machineIds.filter((id) => machineIds.has(id)),
                }));
            }
        });
        return () => { cancelled = true; };
    }, [open, form.warehouseId]);

    // Operators, for the wage rates of the operators linked to the picked machines
    const [operators, setOperators] = useState<OperatorResponse[]>([]);
    useEffect(() => {
        if (!open) return;
        getAllOperators().then((r) => setOperators(r.data ?? [])).catch(() => setOperators([]));
    }, [open]);

    // Production / Quality / Maintenance cost centers only (keep the current one even if it no longer fits)
    const costCenterOptions = costCenters.filter((cc) =>
        (cc.is_active && WORK_CENTER_COST_CENTER_TYPES.includes(String(cc.type).toUpperCase())) || cc.id === form.costCenterId);
    const activeShifts = shifts.filter((s) => s.is_active || form.shiftIds.includes(s.id));
    const selectedShiftHours = shifts
        .filter((s) => form.shiftIds.includes(s.id))
        .reduce((sum, s) => sum + shiftHours(s), 0);
    // With shifts linked, working hours come from them and cannot be typed (the server does the same)
    const hoursFromShifts = selectedShiftHours > 0;
    const shiftHoursValue = fmtHours(Math.min(24, selectedShiftHours));
    useEffect(() => {
        if (!open || !hoursFromShifts) return;
        setForm((p) => (p.workingHoursPerDay === shiftHoursValue ? p : { ...p, workingHoursPerDay: shiftHoursValue }));
    }, [open, hoursFromShifts, shiftHoursValue]);
    const selectedType = typeOptions.find((t) => t.value === form.type);
    // Manual work center (type without machines): people and their standard rate instead of machines.
    // Machine work center: operators per machine and their standard rate, on top of the machine rate.
    const isManual = !!selectedType && !selectedType.machineRequired;
    const isMachineWc = !!selectedType?.machineRequired;
    // Machine + labour (BOTH): persons are extra labour, besides the operators linked to each machine
    const isBoth = isMachineWc && String(form.type).toUpperCase() === 'BOTH';
    const isMachineOnly = isMachineWc && !isBoth;
    const persons = Number(form.personsRequired) || 0;
    const labourRate = Number(form.stdLabourRate) || 0;
    const effectiveHours = (Number(form.workingHoursPerDay) || 0) * ((Number(form.efficiencyPct) || 0) / 100);

    // Active machines of the chosen warehouse; ones already picked always stay listed
    const machineOptions = machines.filter((m) => m.is_active || form.machineIds.includes(m.id));
    const machineSelectOptions: MultiSelectOption[] = machineOptions.map((m) => {
        // A machine sits on one work center; picking it here moves it from the other one
        const elsewhere = m.work_center_id && m.work_center_id !== editing?.id
            ? (m.work_center_code || m.work_center_name)
            : null;
        return {
            value: m.id,
            label: [m.machine_code, m.name].filter(Boolean).join(' · '),
            sublabel: elsewhere
                ? `Now on ${elsewhere} — selecting moves it here`
                : [m.model, !m.warehouse_id && form.warehouseId ? 'No warehouse set' : ''].filter(Boolean).join(' · ') || undefined,
            badge: m.status,
            isActive: m.is_active,
        };
    });
    // Selected machines with their full machine-master record, in the order they were picked
    const selectedMachines = form.machineIds
        .map((id) => machines.find((m) => m.id === id))
        .filter((m): m is MachineResponse => !!m);
    const machineRate = (m: MachineResponse) => Number(m.machine_hour_rate) || 0;
    const totalMachineCapacity = selectedMachines.reduce((a, m) => a + (Number(m.capacity_per_hour) || 0), 0);
    const totalMachineRate = selectedMachines.reduce((a, m) => a + machineRate(m), 0);
    const zeroRateCount = selectedMachines.filter((m) => machineRate(m) === 0).length;
    // Operators linked to machines, from either side: the machine's assigned operators (Machine
    // master) and operators whose allowed machines include it (Operator master). They suggest the
    // operators per machine (average linked per machine) and the labour rate: the sum of their
    // wages goes into the field, their average wage is offered next to it.
    const linkedFor = (machineIds: string[]) => {
        const perMachine = machineIds.map((mid) => {
            const m = machines.find((x) => x.id === mid);
            return new Set([
                ...(m?.assigned_operator_ids ?? []).map(String),
                ...operators.filter((o) => (o.allowed_machine_ids ?? []).map(String).includes(mid)).map((o) => String(o.id)),
            ]);
        }).filter((ids) => ids.size > 0);
        const ids = [...new Set(perMachine.flatMap((set) => [...set]))];
        const ops = ids.map((id) => operators.find((o) => String(o.id) === id)).filter((o): o is OperatorResponse => !!o);
        const wages = ops.map((o) => Number(o.wage_rate_per_hour) || 0).filter((w) => w > 0);
        return {
            operatorIds: ids,
            operators: ops,
            persons: perMachine.length ? Math.max(1, Math.round(perMachine.reduce((a, set) => a + set.size, 0) / perMachine.length)) : null,
            rate: wages.length ? Math.round((wages.reduce((a, w) => a + w, 0) / wages.length) * 100) / 100 : null,
            totalRate: wages.length ? Math.round(wages.reduce((a, w) => a + w, 0) * 100) / 100 : null,
        };
    };
    const linked = linkedFor(form.machineIds);
    /** Operator cost per hour of one machine, by the chosen basis (total or average wage) */
    const operatorWageOf = (machineId: string) => {
        const link = linkedFor([machineId]);
        return (form.operatorWageBasis === 'AVG' ? link.rate : link.totalRate) ?? 0;
    };
    const operatorWageLabel = form.operatorWageBasis === 'AVG' ? 'avg wage' : 'total wages';

    // Standard hourly cost of the work center, worked out the same way as the server does for the list:
    // one operation on one machine, the machines taken at their average (more machines ≠ higher rate)
    const formStdCost = (() => {
        const labourOnce = persons * labourRate;
        if (isManual) return { labour: labourOnce, operators: 0, machines: 0, total: labourOnce };
        const count = selectedMachines.length;
        const machinesCost = count ? totalMachineRate / count : 0;
        if (isBoth) {
            const ops = count ? selectedMachines.reduce((a, m) => a + operatorWageOf(m.id), 0) / count : 0;
            return { labour: labourOnce, operators: ops, machines: machinesCost, total: labourOnce + ops + machinesCost };
        }
        return { labour: labourOnce, operators: 0, machines: machinesCost, total: labourOnce + machinesCost };
    })();
    const avgNote = selectedMachines.length > 1 ? ` (avg of ${selectedMachines.length})` : '';
    const linkedOperatorIds = linked.operatorIds;
    const linkedOperators = linked.operators;
    const suggestedPersons = linked.persons;
    const suggestedRate = linked.rate;
    const suggestedTotalRate = linked.totalRate;

    // Picking / removing machines fetches the linked operators' rate into the labour rate (the user
    // can still change it afterwards); operators per machine is filled when still empty
    const changeMachines = (machineIds: string[]) => {
        if (!isMachineOnly) {
            // Machine + labour: the operators' wages are counted per machine, not in the labour rate
            setForm((p) => ({ ...p, machineIds }));
            return;
        }
        const next = linkedFor(machineIds);
        setForm((p) => ({
            ...p,
            machineIds,
            ...(next.totalRate != null ? { stdLabourRate: String(next.totalRate) } : {}),
            ...(next.persons && !p.personsRequired ? { personsRequired: String(next.persons) } : {}),
        }));
        if (next.totalRate != null || next.persons) {
            setErrors((e) => {
                if (!e.stdLabourRate && !e.personsRequired) return e;
                const n = { ...e };
                delete n.stdLabourRate;
                delete n.personsRequired;
                return n;
            });
        }
    };
    const removeMachine = (id: string) => changeMachines(form.machineIds.filter((x) => x !== id));

    // An opened work center with empty fields takes the suggestion once its data has loaded
    useEffect(() => {
        if (!open || !isMachineOnly) return;
        setForm((p) => ({
            ...p,
            personsRequired: p.personsRequired || (suggestedPersons ? String(suggestedPersons) : ''),
            stdLabourRate: p.stdLabourRate || (suggestedTotalRate ? String(suggestedTotalRate) : ''),
        }));
    }, [open, isMachineOnly, suggestedPersons, suggestedTotalRate]);

    // Selected shifts with their full shift-master record, in the order they were picked
    const selectedShifts = form.shiftIds
        .map((id) => shifts.find((s) => s.id === id))
        .filter((s): s is ShiftResponse => !!s);
    const coveredDays = new Set(selectedShifts.flatMap((s) => (s.working_days || []).map(dayKey)));
    const overlappingShifts = selectedShifts.flatMap((a, i) =>
        selectedShifts.slice(i + 1).filter((b) => shiftsOverlap(a, b)).map((b) => `${a.name} & ${b.name}`));
    const removeShift = (id: string) => setForm((p) => ({ ...p, shiftIds: p.shiftIds.filter((x) => x !== id) }));

    const shiftSelectOptions: MultiSelectOption[] = activeShifts.map((s) => ({
        value: s.id,
        label: s.name,
        sublabel: `${String(s.start_time).slice(0, 5)}–${String(s.end_time).slice(0, 5)} · ${fmtHours(shiftHours(s))} h after break${(s.working_days || []).length ? ` · ${(s.working_days || []).join(', ')}` : ''}`,
        badge: !s.warehouse_id && form.warehouseId ? 'No warehouse set' : undefined,
        isActive: s.is_active,
    }));

    // const addType = async () => {
    //     if (!newTypeInput.trim()) return;
    //     const val = await onAddNewType(newTypeInput.trim(), newTypeNeedsMachine);
    //     if (!val) return;
    //     update('type', val);
    //     setNewTypeInput('');
    //     setNewTypeNeedsMachine(false);
    //     setIsAddingType(false);
    // };

    useEffect(() => {
        if (!open) return;
        pruneOnLoadRef.current = false;
        if (editing) {
            setForm({
                code: editing?.code || '',
                name: editing?.name || '',
                type: editing?.type || 'MACHINE',
                warehouseId: editing?.warehouseId ?? '',
                description: editing?.description ?? '',
                isActive: editing?.isActive ?? true,
                costCenterId: editing?.costCenterId ?? '',
                workingHoursPerDay: String(editing?.workingHoursPerDay ?? 8),
                efficiencyPct: String(editing?.efficiencyPct ?? 100),
                shiftIds: editing?.shiftIds ?? [],
                machineIds: editing?.machineIds ?? [],
                personsRequired: editing?.personsRequired != null ? String(editing.personsRequired) : '',
                stdLabourRate: editing?.stdLabourRate != null ? String(editing.stdLabourRate) : '',
                operatorWageBasis: editing?.operatorWageBasis ?? 'TOTAL',
            });
        } else {
            setForm({ ...emptyForm, warehouseId: defaultWarehouseId || '' });
            // Suggest the next code in the series; it can be changed before saving
            getNextWorkCenterCode()
                .then((code) => setForm((p) => (p.code ? p : { ...p, code })))
                .catch(() => {});
        }
        setErrors({});
        setTimeout(() => firstInputRef.current?.focus(), 60);
    }, [open, editing]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    const update = (field: keyof WCForm, value: string | boolean) => {
        setForm((p) => ({ ...p, [field]: value }));
        if (errors[field]) setErrors((e) => { const n = { ...e }; delete n[field]; return n; });
    };

    const validate = () => {
        const e: Record<string, string> = {};
        if (form.code.trim() && !CODE_PATTERN.test(form.code.trim().toUpperCase())) {
            e.code = 'Use letters, digits, - _ / (max 20)';
        }
        if (!form.name.trim()) e.name = 'Work center name is required';
        const hours = Number(form.workingHoursPerDay);
        if (!form.workingHoursPerDay.trim() || !(hours > 0) || hours > 24) e.workingHoursPerDay = 'Enter hours between 0 and 24';
        const eff = Number(form.efficiencyPct);
        if (!form.efficiencyPct.trim() || !(eff > 0) || eff > 200) e.efficiencyPct = 'Enter an efficiency between 1 and 200%';
        if (isManual || isMachineWc) {
            const persons = Number(form.personsRequired);
            if (!form.personsRequired.trim()) e.personsRequired = `${isMachineOnly ? 'Operators per machine' : 'Persons required'} is mandatory`;
            else if (!Number.isInteger(persons) || persons < 1) e.personsRequired = 'Enter a whole number, at least 1';
            if (form.stdLabourRate.trim() && !(Number(form.stdLabourRate) >= 0)) e.stdLabourRate = 'Enter a rate of 0 or more';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await onSave(form);
        } catch (error) {
            // Keep SlideOver open to let user correct any server validation issues
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[600] flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative bg-white w-full max-w-lg flex flex-col h-full shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
                    <div>
                        <h3 className="text-base font-semibold text-[#1e293b]">
                            {editing ? 'Edit Work Center' : 'Add Work Center'}
                        </h3>
                        <p className="text-xs text-[#64748b] mt-0.5">
                            {editing ? 'Update work center details' : 'Create a new work center'}
                        </p>
                    </div>
                    <button onClick={onClose}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] cursor-pointer">
                        <i className="ri-close-line" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                     
                     {/* Warehouse */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Warehouse / Location</label>
                        <select
                            value={form.warehouseId}
                            onChange={(e) => {
                                pruneOnLoadRef.current = true;
                                update('warehouseId', e.target.value);
                            }}
                            className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 cursor-pointer"
                        >
                            <option value="">— Select Warehouse —</option>
                            {warehouses.filter((w) => w.is_active !== false).map((w) => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Code */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                            Work Center Code
                        </label>
                        <input
                            type="text" value={form.code}
                            onChange={(e) => update('code', e.target.value.toUpperCase())}
                            maxLength={20}
                            placeholder="Auto (WC-001)"
                            className={`w-full h-10 px-3 rounded-lg border text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.code ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                        />
                        {errors.code
                            ? <p className="text-xs text-red-500">{errors.code}</p>
                            : <p className="text-xs text-[#94a3b8]">Short code shown in routing, schedule and reports. Leave empty for the next auto code.</p>}
                    </div>

                    {/* Name */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                            Work Center Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            ref={firstInputRef}
                            type="text" value={form.name}
                            onChange={(e) => update('name', e.target.value)}
                            placeholder="e.g. Cutting Area, Assembly Line 1"
                            className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.name ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                        />
                        {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                    </div>

                    {/* Type */}
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Type <span className="text-red-500">*</span>
                            </label>
                            {/* <button
                                type="button"
                                onClick={() => setIsAddingType((p) => !p)}
                                title="Add new work center type"
                                className="flex items-center gap-1 px-2 py-0.5 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-[#4f46e5] text-xs font-semibold transition-colors cursor-pointer"
                            >
                                <i className="ri-add-line text-sm font-bold" />
                                <span>Add Type</span>
                            </button> */}
                        </div>

                        {/* {isAddingType && (
                            <div className="flex items-center gap-2 p-2 rounded-lg bg-indigo-50/50 border border-indigo-200 animate-in fade-in duration-150">
                                <input
                                    type="text"
                                    value={newTypeInput}
                                    onChange={(e) => setNewTypeInput(e.target.value)}
                                    placeholder="Enter new type (e.g. Assembly, Packaging)..."
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            void addType();
                                        } else if (e.key === 'Escape') {
                                            setIsAddingType(false);
                                            setNewTypeInput('');
                                        }
                                    }}
                                    className="flex-1 h-8 px-2.5 rounded border border-slate-300 bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5]"
                                />
                                <label className="flex items-center gap-1 text-xs text-[#475569] whitespace-nowrap cursor-pointer" title="Routing will ask for a machine on stages using this type">
                                    <input
                                        type="checkbox"
                                        checked={newTypeNeedsMachine}
                                        onChange={(e) => setNewTypeNeedsMachine(e.target.checked)}
                                        className="w-3.5 h-3.5 accent-[#4f46e5]"
                                    />
                                    Machine required?
                                </label>
                                <button
                                    type="button"
                                    onClick={() => void addType()}
                                    className="h-8 px-3 rounded bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap"
                                >
                                    Add
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsAddingType(false);
                                        setNewTypeInput('');
                                    }}
                                    className="h-8 px-2 rounded border border-slate-300 text-xs text-[#64748b] hover:bg-white cursor-pointer"
                                >
                                    Cancel
                                </button>
                            </div>
                        )} */}

                        <div className="flex flex-wrap gap-2 pt-0.5">
                            {typeOptions.map((t) => {
                                const badge = getTypeBadge(t.value);
                                const isSelected = form.type === t.value;
                                return (
                                    <button
                                        key={t.value}
                                        type="button"
                                        onClick={() => update('type', t.value)}
                                        className={`flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${isSelected
                                            ? `${badge.cls} border-current ring-2 ring-current/20 shadow-sm`
                                            : 'border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5]/40 hover:text-[#4f46e5] bg-white'
                                            }`}
                                    >
                                        {t.label}
                                        {t.machineRequired && <i className="ri-settings-3-line text-[11px] opacity-70" title="Needs a machine in routing" />}
                                    </button>
                                );
                            })}
                        </div>
                        {selectedType && (
                            selectedType.isSystem ? (
                                <p className="text-xs text-[#94a3b8]">
                                    {selectedType.machineRequired ? 'Routing stages on this work center need a machine.' : 'Routing stages on this work center need no machine.'}
                                </p>
                            ) : (
                                <p className="flex items-start gap-1 text-xs text-amber-600">
                                    <i className="ri-error-warning-line mt-px" />
                                    “{selectedType.label}” is no longer a work center type — choose Machine, Labor or Both.
                                </p>
                            )
                        )}
                    </div>

                    {/* Assigned machines (types that use machines only) */}
                    {selectedType?.machineRequired && (
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Link Machines</label>
                            <SearchableMultiSelect
                                value={form.machineIds}
                                onChange={changeMachines}
                                options={machineSelectOptions}
                                placeholder={loadingResources ? 'Loading machines…' : '+ Add Machine'}
                                searchPlaceholder="Search by code or name..."
                                emptyText={form.warehouseId ? 'No machines in this warehouse — add them under Masters → Machines' : 'No machines yet — add them under Masters → Machines'}
                                noneSelectedText="No machines on this work center"
                                icon="ri-settings-3-line"
                                direction="auto"
                                maxDisplayedChips={4}
                            />
                            {form.machineIds.length === 0 ? (
                                <p className="flex items-start gap-1 text-xs text-amber-600">
                                    <i className="ri-error-warning-line mt-px" />
                                    Routing stages on this work center must pick one of its machines — link at least one.
                                </p>
                            ) : (
                                <p className="text-xs text-[#94a3b8]">Routing, work orders and shop floor offer only these machines for this work center.</p>
                            )}

                            {selectedMachines.length > 0 && (
                                <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] overflow-hidden">
                                    {/* Summary */}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 border-b border-[#e2e8f0] bg-white text-xs">
                                        <span className="font-semibold text-[#1e293b]">
                                            {selectedMachines.length} machine{selectedMachines.length > 1 ? 's' : ''}
                                        </span>
                                        <span className="text-[#64748b]">
                                            Capacity <strong className="text-[#1e293b]">{fmtHours(totalMachineCapacity)}</strong> units/hr
                                        </span>
                                        <span className="text-[#64748b]">
                                            Running cost <strong className="text-[#1e293b]">₹{fmtHours(totalMachineRate)}</strong>/hr
                                        </span>
                                    </div>

                                    {/* One card per machine */}
                                    <div className="divide-y divide-[#e2e8f0]">
                                        {selectedMachines.map((m) => {
                                            const next = nextMaintenance(m);
                                            const movesFrom = m.work_center_id && m.work_center_id !== editing?.id
                                                ? (m.work_center_code || m.work_center_name)
                                                : null;
                                            return (
                                                <div key={m.id} className="px-3 py-2.5 space-y-1.5">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-semibold text-[#1e293b] truncate">
                                                                {m.machine_code && <span className="font-mono text-[#4f46e5] mr-1.5">{m.machine_code}</span>}
                                                                {m.name}
                                                            </p>
                                                            <p className="text-xs text-[#94a3b8] truncate">
                                                                {[m.make, m.model].filter(Boolean).join(' · ') || 'No make / model'}
                                                                {m.serial_number && ` · S/N ${m.serial_number}`}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 shrink-0">
                                                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${MACHINE_STATUS_BADGE[m.status] || MACHINE_STATUS_BADGE.IDLE}`}>
                                                                {m.status}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeMachine(m.id)}
                                                                title="Remove from this work center"
                                                                className="w-6 h-6 flex items-center justify-center rounded text-[#94a3b8] hover:text-red-500 hover:bg-red-50 cursor-pointer"
                                                            >
                                                                <i className="ri-close-line" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Capacity</p>
                                                            <p className="text-[#1e293b]">{m.capacity_per_hour != null ? `${m.capacity_per_hour} /hr` : '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Hour rate</p>
                                                            <p className={machineRate(m) === 0 ? 'text-amber-600' : 'text-[#1e293b]'}>₹{fmtHours(machineRate(m))}/hr</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Next PM</p>
                                                            <p className={next?.overdue ? 'text-red-600 font-semibold' : 'text-[#1e293b]'} title={`Last maintenance: ${fmtDate(m.last_maintenance_date)}`}>
                                                                {next ? `${fmtDate(next.date.toISOString())}${next.overdue ? ' (overdue)' : ''}` : '—'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {(() => {
                                                        const link = linkedFor([m.id]);
                                                        return (
                                                            <>
                                                                {link.operators.length > 0 ? (
                                                                    <p className="flex items-start gap-1 text-[11px] text-[#64748b]">
                                                                        <i className="ri-team-line mt-px" />
                                                                        <span>{link.operators.map((o) => `${o.name} ₹${Number(o.wage_rate_per_hour) || 0}/hr`).join(', ')}</span>
                                                                    </p>
                                                                ) : (
                                                                    <p className="text-[11px] text-[#94a3b8]">No operator linked</p>
                                                                )}
                                                                {isBoth && (
                                                                    <p className="text-[11px] text-[#475569]">
                                                                        Operators ({operatorWageLabel}) ₹{fmtHours(operatorWageOf(m.id))} + machine ₹{fmtHours(machineRate(m))} = <strong className="text-[#1e293b]">₹{fmtHours(operatorWageOf(m.id) + machineRate(m))}/hr</strong>
                                                                    </p>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                    {movesFrom && (
                                                        <p className="flex items-center gap-1 text-[11px] text-amber-600">
                                                            <i className="ri-arrow-left-right-line" /> Moves here from {movesFrom} on save
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {zeroRateCount > 0 && (
                                        <p className="flex items-start gap-1 px-3 py-2 border-t border-[#e2e8f0] bg-amber-50 text-[11px] text-amber-700">
                                            <i className="ri-error-warning-line mt-px" />
                                            {zeroRateCount} machine{zeroRateCount > 1 ? 's have' : ' has'} a ₹0 hour rate — machine cost will be ₹0 in costing for work on {zeroRateCount > 1 ? 'them' : 'it'}.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* People: persons (manual) or operators per machine, with the standard labour rate */}
                    {(isManual || isMachineWc) && (
                        <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                        {isMachineOnly ? 'Operators per Machine' : 'Persons Required'} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number" min="1" step="1"
                                        value={form.personsRequired}
                                        onKeyDown={(e) => { if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault(); }}
                                        onChange={(e) => update('personsRequired', e.target.value.replace(/[^0-9]/g, ''))}
                                        placeholder={isMachineOnly ? 'e.g. 1' : isBoth ? 'e.g. 2' : 'e.g. 4'}
                                        className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.personsRequired ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                                    />
                                    {errors.personsRequired ? (
                                        <p className="text-xs text-red-500">{errors.personsRequired}</p>
                                    ) : isBoth ? (
                                        <p className="text-xs text-[#94a3b8]">Labour besides the machines' own operators</p>
                                    ) : isMachineOnly && suggestedPersons ? (
                                        <p className="text-xs text-[#94a3b8]">
                                            {linkedOperatorIds.length} operator{linkedOperatorIds.length > 1 ? 's' : ''} linked to these machines
                                            {String(suggestedPersons) !== form.personsRequired && (
                                                <> · <button type="button" onClick={() => update('personsRequired', String(suggestedPersons))} className="text-[#4f46e5] hover:underline cursor-pointer">use {suggestedPersons}</button></>
                                            )}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                        Std Labour Rate / Person (₹/hr)
                                    </label>
                                    <input
                                        type="number" min="0" step="0.01"
                                        value={form.stdLabourRate}
                                        onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
                                        onChange={(e) => update('stdLabourRate', e.target.value.replace(/-/g, ''))}
                                        placeholder={isMachineWc ? 'e.g. 200' : 'e.g. 180'}
                                        className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.stdLabourRate ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                                    />
                                    {errors.stdLabourRate ? (
                                        <p className="text-xs text-red-500">{errors.stdLabourRate}</p>
                                    ) : isMachineOnly && suggestedRate != null ? (
                                        <p className="text-xs text-[#94a3b8]" title={linkedOperators.map((o) => `${o.name}: ₹${Number(o.wage_rate_per_hour) || 0}/hr`).join('\n')}>
                                            Linked operators: total ₹{fmtHours(suggestedTotalRate ?? 0)}/hr · avg ₹{fmtHours(suggestedRate)}/hr
                                            {String(suggestedRate) !== form.stdLabourRate && (
                                                <> · <button type="button" onClick={() => update('stdLabourRate', String(suggestedRate))} className="text-[#4f46e5] hover:underline cursor-pointer">use avg</button></>
                                            )}
                                            {suggestedTotalRate != null && String(suggestedTotalRate) !== form.stdLabourRate && (
                                                <> · <button type="button" onClick={() => update('stdLabourRate', String(suggestedTotalRate))} className="text-[#4f46e5] hover:underline cursor-pointer">use total</button></>
                                            )}
                                        </p>
                                    ) : isMachineOnly ? (
                                        <p className="text-xs text-[#94a3b8]">No linked operator wage — enter the rate manually</p>
                                    ) : null}
                                </div>
                            </div>

                            {/* Std hourly cost (preview) */}
                            {persons > 0 && isBoth && (
                                <div className="rounded-lg bg-indigo-50/60 border border-indigo-100 text-xs overflow-hidden">
                                    <div className="flex items-center justify-between gap-2 px-3 py-2">
                                        <span className="text-[#64748b]">Std hourly cost (preview)</span>
                                        <span className="font-semibold text-[#1e293b]">
                                            labour {persons} × ₹{fmtHours(labourRate)} + operators + machine rate
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 px-3 pb-2">
                                        <span className="text-[#64748b]">Operator wage per machine</span>
                                        <div className="inline-flex rounded-md border border-indigo-200 bg-white p-0.5">
                                            {([['TOTAL', 'Total of linked'], ['AVG', ]] as const).map(([value, label]) => (
                                                <button
                                                    key={value}
                                                    type="button"
                                                    onClick={() => setForm((p) => ({ ...p, operatorWageBasis: value }))}
                                                    className={`h-6 px-2 rounded text-[11px] font-semibold cursor-pointer transition-colors ${form.operatorWageBasis === value ? 'bg-[#4f46e5] text-white' : 'text-[#64748b] hover:text-[#4f46e5]'}`}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {selectedMachines.length > 0 && (
                                        <div className="border-t border-indigo-100 divide-y divide-indigo-100/70">
                                            {selectedMachines.map((m) => {
                                                const ops = operatorWageOf(m.id);
                                                return (
                                                    <div key={m.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                                                        <span className="text-[#475569] truncate">
                                                            {m.machine_code && <span className="font-mono mr-1">{m.machine_code}</span>}{m.name}
                                                        </span>
                                                        <span className="text-[#1e293b] whitespace-nowrap" title={`Labour + linked operators' ${operatorWageLabel} + machine hour rate`}>
                                                            ₹{fmtHours(persons * labourRate)} + ₹{fmtHours(ops)} + ₹{fmtHours(machineRate(m))} = <strong className="text-[#4f46e5]">₹{fmtHours(persons * labourRate + ops + machineRate(m))}/hr</strong>
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                            {selectedMachines.length > 1 && (
                                                <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-white/60">
                                                    <span className="text-[#64748b]">All {selectedMachines.length} machines running (labour once)</span>
                                                    <strong className="text-[#1e293b]">
                                                        ₹{fmtHours(persons * labourRate + selectedMachines.reduce((a, m) => a + operatorWageOf(m.id) + machineRate(m), 0))}/hr
                                                    </strong>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {persons > 0 && isMachineOnly && (
                                <div className="rounded-lg bg-indigo-50/60 border border-indigo-100 text-xs overflow-hidden">
                                    <div className="flex items-center justify-between gap-2 px-3 py-2">
                                        <span className="text-[#64748b]">Std hourly cost (preview)</span>
                                        <span className="font-semibold text-[#1e293b]">
                                            {persons} × ₹{fmtHours(labourRate)} + machine rate
                                        </span>
                                    </div>
                                    {selectedMachines.length > 0 && (
                                        <div className="border-t border-indigo-100 divide-y divide-indigo-100/70">
                                            {selectedMachines.map((m) => (
                                                <div key={m.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                                                    <span className="text-[#475569] truncate">
                                                        {m.machine_code && <span className="font-mono mr-1">{m.machine_code}</span>}{m.name}
                                                    </span>
                                                    <span className="text-[#1e293b] whitespace-nowrap">
                                                        {persons} × ₹{fmtHours(labourRate)} + ₹{fmtHours(machineRate(m))} = <strong className="text-[#4f46e5]">₹{fmtHours(persons * labourRate + machineRate(m))}/hr</strong>
                                                    </span>
                                                </div>
                                            ))}
                                            {selectedMachines.length > 1 && (
                                                <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-white/60">
                                                    <span className="text-[#64748b]">All {selectedMachines.length} machines running</span>
                                                    <strong className="text-[#1e293b]">₹{fmtHours(selectedMachines.length * persons * labourRate + totalMachineRate)}/hr</strong>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Work center total: the same figure the list shows after saving */}
                            <div className="rounded-lg border border-[#4f46e5]/30 bg-white px-3 py-2.5 space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Work center std cost</span>
                                    <span className="text-base font-bold text-[#4f46e5]">₹{fmtHours(formStdCost.total)}/hr</span>
                                </div>
                                <p className="text-[11px] text-[#64748b]">
                                    {isManual
                                        ? `Labour ${persons} × ₹${fmtHours(labourRate)} = ₹${fmtHours(formStdCost.labour)}`
                                        : isBoth
                                            ? `Labour ₹${fmtHours(formStdCost.labour)} + operators (${operatorWageLabel}${avgNote}) ₹${fmtHours(formStdCost.operators)} + machine rate${avgNote} ₹${fmtHours(formStdCost.machines)}`
                                            : `Operators ${persons} × ₹${fmtHours(labourRate)} = ₹${fmtHours(formStdCost.labour)} + machine rate${avgNote} ₹${fmtHours(formStdCost.machines)}`}
                                </p>
                            </div>
                            {persons > 0 && form.stdLabourRate.trim() !== '' && labourRate === 0 && (
                                <p className="flex items-start gap-1 text-xs text-amber-600">
                                    <i className="ri-error-warning-line mt-px" />
                                    Labour on this work center is costed at ₹0 when its operator has no wage rate.
                                </p>
                            )}
                            <p className="text-xs text-[#94a3b8]">
                                {isManual
                                    ? 'Manual work: routing stages here need no machine and start with this crew; no machine can be assigned to this work center.'
                                    : isBoth
                                        ? 'Machine + labour: routing stages here must pick one of the linked machines; the machine\'s operators run it, plus this labour crew.'
                                        : 'Machine work: routing stages here must pick one of the linked machines and start with this many operators.'}
                            </p>
                        </div>
                    )}

                    {/* Cost Center */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Cost Center</label>
                        <select
                            value={form.costCenterId}
                            onChange={(e) => update('costCenterId', e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 cursor-pointer"
                        >
                            <option value="">— None —</option>
                            {costCenterOptions.map((cc) => (
                                <option key={cc.id} value={cc.id}>
                                    {cc.code ? `${cc.code} · ` : ''}{cc.name} ({String(cc.type).charAt(0) + String(cc.type).slice(1).toLowerCase()})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-[#94a3b8]">Department grouping — only Production, Quality and Maintenance cost centers are listed.</p>
                    </div>

                    {/* Shifts */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Shift(s)</label>
                        <SearchableMultiSelect
                            value={form.shiftIds}
                            onChange={(ids) => setForm((p) => ({ ...p, shiftIds: ids }))}
                            options={shiftSelectOptions}
                            placeholder={loadingResources ? 'Loading shifts…' : '— Select shifts —'}
                            searchPlaceholder="Search shifts..."
                            emptyText={form.warehouseId ? 'No shifts in this warehouse — add them under Masters → Shifts' : 'No shifts yet — add them under Masters → Shifts'}
                            noneSelectedText="No shift selected — every day is a working day"
                            icon="ri-time-line"
                            direction="auto"
                            maxDisplayedChips={4}
                        />
                        <p className="text-xs text-[#94a3b8]">The schedule plans work only on these shifts' working days. None selected = every day.</p>

                        {selectedShifts.length > 0 && (
                            <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] overflow-hidden">
                                {/* Summary */}
                                <div className="px-3 py-2 border-b border-[#e2e8f0] bg-white space-y-1.5">
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                        <span className="font-semibold text-[#1e293b]">
                                            {selectedShifts.length} shift{selectedShifts.length > 1 ? 's' : ''}
                                        </span>
                                        <span className="text-[#64748b]">
                                            <strong className="text-[#1e293b]">{fmtHours(selectedShiftHours)} h</strong>/day after breaks
                                        </span>
                                        <span className="text-[#64748b]">
                                            <strong className="text-[#1e293b]">{coveredDays.size}</strong> working day{coveredDays.size === 1 ? '' : 's'}/week
                                        </span>
                                    </div>
                                    <div className="flex gap-1">
                                        {WEEK_DAYS.map((d) => (
                                            <span
                                                key={d}
                                                className={`w-9 text-center text-[10px] font-semibold py-0.5 rounded ${coveredDays.has(d) ? 'bg-indigo-50 text-[#4f46e5] border border-indigo-200' : 'bg-white text-[#cbd5e1] border border-[#e2e8f0]'}`}
                                            >
                                                {d}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* One card per shift */}
                                <div className="divide-y divide-[#e2e8f0]">
                                    {selectedShifts.map((s) => {
                                        const span = shiftSpan(s);
                                        const days = new Set((s.working_days || []).map(dayKey));
                                        return (
                                            <div key={s.id} className="px-3 py-2.5 space-y-1.5">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-semibold text-[#1e293b] truncate">{s.name}</p>
                                                        <p className="text-xs text-[#94a3b8] truncate">
                                                            {s.warehouse_name || (s.warehouse_id ? 'Warehouse' : 'No warehouse set')}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        {!s.is_active && (
                                                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">INACTIVE</span>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeShift(s.id)}
                                                            title="Remove from this work center"
                                                            className="w-6 h-6 flex items-center justify-center rounded text-[#94a3b8] hover:text-red-500 hover:bg-red-50 cursor-pointer"
                                                        >
                                                            <i className="ri-close-line" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2 text-xs">
                                                    <div>
                                                        <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Timing</p>
                                                        <p className="text-[#1e293b]">
                                                            {String(s.start_time).slice(0, 5)}–{String(s.end_time).slice(0, 5)}
                                                            {span.overnight && <span className="text-[10px] text-[#94a3b8]"> +1 day</span>}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Break</p>
                                                        <p className="text-[#1e293b]">{Number(s.break_minutes) || 0} min</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Working</p>
                                                        <p className="text-[#1e293b]">{fmtHours(shiftHours(s))} h</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    {WEEK_DAYS.map((d) => (
                                                        <span
                                                            key={d}
                                                            className={`w-9 text-center text-[10px] font-semibold py-0.5 rounded ${days.has(d) ? 'bg-[#4f46e5] text-white' : 'bg-white text-[#cbd5e1] border border-[#e2e8f0]'}`}
                                                        >
                                                            {d}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {overlappingShifts.length > 0 && (
                                    <p className="flex items-start gap-1 px-3 py-2 border-t border-[#e2e8f0] bg-amber-50 text-[11px] text-amber-700">
                                        <i className="ri-error-warning-line mt-px" />
                                        Overlapping times on a shared day: {overlappingShifts.join(', ')} — the hours above count the overlap twice.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Working hours + efficiency */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Working Hours / Day <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number" min="0" max="24" step="0.5"
                                value={form.workingHoursPerDay}
                                onChange={(e) => update('workingHoursPerDay', e.target.value)}
                                readOnly={hoursFromShifts}
                                title={hoursFromShifts ? 'Worked out from the selected shifts (start → end − break)' : undefined}
                                className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] focus:outline-none focus:ring-2 transition-colors ${hoursFromShifts ? 'bg-[#f8fafc] cursor-not-allowed' : 'bg-white'} ${errors.workingHoursPerDay ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                            />
                            {errors.workingHoursPerDay ? (
                                <p className="text-xs text-red-500">{errors.workingHoursPerDay}</p>
                            ) : hoursFromShifts ? (
                                <p className="text-xs text-[#94a3b8]">
                                    From selected shifts{selectedShiftHours > 24 ? ' (capped at 24 h)' : ''} — remove the shifts to enter hours manually
                                </p>
                            ) : null}
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Efficiency % <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number" min="1" max="200"
                                value={form.efficiencyPct}
                                onChange={(e) => update('efficiencyPct', e.target.value)}
                                className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.efficiencyPct ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                            />
                            {errors.efficiencyPct && <p className="text-xs text-red-500">{errors.efficiencyPct}</p>}
                        </div>
                    </div>
                    {effectiveHours > 0 && (
                        <p className="text-xs text-[#64748b] -mt-2">
                            Plannable time: <strong>{fmtHours(effectiveHours)} h/day</strong> ({form.workingHoursPerDay} h × {form.efficiencyPct}%)
                        </p>
                    )}

                    {/* Description */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Description</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => update('description', e.target.value)}
                            placeholder="Brief description of this work center"
                            rows={3}
                            className="w-full px-3 py-2.5 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 resize-none"
                        />
                    </div>

                    {/* Active toggle */}
                    <div className="pt-2">
                        <MasterToggle
                            checked={form.isActive}
                            onChange={(val) => update('isActive', val)}
                            activeLabel="Active — work center is operational"
                            inactiveLabel="Inactive — work center is disabled"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-[#f8fafc]">
                    <button onClick={onClose}
                        className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-white transition-colors cursor-pointer whitespace-nowrap">
                        Cancel
                    </button>
                    <button onClick={handleSave} disabled={isSaving}
                        className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap">
                        {isSaving
                            ? <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                            : <><i className="ri-save-line" /> {editing ? 'Update' : 'Save'}</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function WorkCentersPage() {
    const navigate = useNavigate();
    const toast = useToast();
    const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
    const [warehouses, setWarehouses] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<WCType | 'ALL'>('ALL');
    const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
    const [slideOver, setSlideOver] = useState<{ open: boolean; editing: WorkCenter | null }>({ open: false, editing: null });
    const [deleteConfirm, setDeleteConfirm] = useState<WorkCenter | null>(null);

    // Work center types live on the server; machineRequired tells routing to ask for a machine
    const [serverTypes, setServerTypes] = useState<WorkCenterTypeDef[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenterResponse[]>([]);

    const loadTypes = async () => {
        try {
            // Only the built-in types (Machine / Labor / Both); custom types kept by an old browser are dropped
            const types = await getWorkCenterTypes();
            try { localStorage.removeItem('workcenter_custom_types'); } catch { /* ignore */ }
            setServerTypes(types);
        } catch {
            setServerTypes([]);
        }
    };

    const typeUsesMachines = (type: string) => allTypeOptions.find((t) => t.value === type)?.machineRequired ?? false;

    const allTypeOptions = useMemo((): TypeOption[] => {
        const list: TypeOption[] = serverTypes.map((t) => ({
            value: t.code,
            label: DEFAULT_TYPE_OPTIONS.find((d) => d.value === t.code)?.label ?? t.label,
            machineRequired: t.machineRequired,
            isSystem: t.isSystem,
        }));
        // A type still on a work center but unknown to the server is listed so the row stays readable
        workCenters.forEach((w) => {
            if (w.type && !list.some((t) => t.value === w.type)) {
                list.push({ value: w.type, label: w.type, machineRequired: false, isSystem: false });
            }
        });
        if (list.length === 0) DEFAULT_TYPE_OPTIONS.forEach((d) => list.push({ ...d, machineRequired: d.value !== 'LABOR', isSystem: true }));
        return list;
    }, [serverTypes, workCenters]);

    const { selectedWarehouseId } = useWarehouseStore();

    const mapApiToWorkCenter = (wc: WorkCenterResponse): WorkCenter => ({
        id: wc.id,
        code: wc.code || '',
        name: wc.name,
        type: wc.type,
        warehouseId: wc.warehouse_id,
        description: wc.description || '',
        isActive: wc.is_active,
        costCenterId: wc.cost_center_id ?? null,
        costCenterName: wc.cost_center_name ? [wc.cost_center_code, wc.cost_center_name].filter(Boolean).join(' · ') : null,
        workingHoursPerDay: Number(wc.working_hours_per_day ?? 8),
        efficiencyPct: Number(wc.efficiency_pct ?? 100),
        shiftIds: wc.shift_ids ?? [],
        shiftNames: (wc.shifts ?? []).map((s) => s.name),
        machineIds: (wc.machines ?? []).map((m) => m.id),
        machineNames: (wc.machines ?? []).map((m) => m.machine_code || m.name),
        personsRequired: wc.persons_required ?? null,
        stdLabourRate: wc.std_labour_rate != null && wc.std_labour_rate !== '' ? Number(wc.std_labour_rate) : null,
        operatorWageBasis: wc.operator_wage_basis === 'AVG' ? 'AVG' : 'TOTAL',
        stdCost: {
            labour: Number(wc.std_labour_cost_per_hour) || 0,
            operators: Number(wc.std_operator_cost_per_hour) || 0,
            machines: Number(wc.std_machine_cost_per_hour) || 0,
            total: Number(wc.std_hourly_cost) || 0,
        },
    });

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [wcRes, whRes] = await Promise.all([
                getAllWorkCenters(),
                getAllWarehouses(),
                loadTypes(),
                getAllCostCenters().then((r) => setCostCenters(r.data ?? [])).catch(() => setCostCenters([])),
            ]);
            if (wcRes.success && wcRes.data) {
                setWorkCenters(wcRes.data.map(mapApiToWorkCenter));
            }
            if (whRes.success && whRes.data) {
                setWarehouses(whRes.data);
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const warehouseWorkCenters = useMemo(() => {
        if (!selectedWarehouseId) return workCenters;
        return workCenters.filter((wc) => wc.warehouseId === selectedWarehouseId);
    }, [workCenters, selectedWarehouseId]);

    const filtered = useMemo(() => {
        return warehouseWorkCenters.filter((wc) => {
            const q = search.toLowerCase();
            const matchSearch = !q || wc.name.toLowerCase().includes(q) || wc.code.toLowerCase().includes(q) || (wc.description && wc.description.toLowerCase().includes(q));
            const matchType = typeFilter === 'ALL' || wc.type === typeFilter;
            const matchStatus = statusFilter === 'ALL' || (statusFilter === 'ACTIVE' ? wc.isActive : !wc.isActive);
            return matchSearch && matchType && matchStatus;
        });
    }, [warehouseWorkCenters, search, typeFilter, statusFilter]);

    // The form loads the machines and shifts of its warehouse itself, each time it opens
    const openAdd = () => setSlideOver({ open: true, editing: null });
    const openEdit = (wc: WorkCenter) => setSlideOver({ open: true, editing: wc });
    const closeSlideOver = () => setSlideOver({ open: false, editing: null });

    const handleSave = async (form: WCForm) => {
        try {
            if (slideOver.editing) {
                const res = await updateWorkCenter(slideOver.editing.id, {
                    code: form.code.trim().toUpperCase(),
                    name: form.name,
                    type: form.type,
                    warehouseId: form.warehouseId || null,
                    description: form.description,
                    isActive: form.isActive,
                    costCenterId: form.costCenterId || null,
                    workingHoursPerDay: Number(form.workingHoursPerDay),
                    efficiencyPct: Number(form.efficiencyPct),
                    shiftIds: form.shiftIds,
                    // A type without machines keeps none
                    machineIds: typeUsesMachines(form.type) ? form.machineIds : [],
                    // Persons (manual) or operators per machine, and their standard rate
                    personsRequired: Number(form.personsRequired) || null,
                    stdLabourRate: form.stdLabourRate.trim() === '' ? null : Number(form.stdLabourRate),
                    operatorWageBasis: form.operatorWageBasis,
                });
                if (res.success && res.data) {
                    setWorkCenters((prev) =>
                        prev.map((wc) =>
                            wc.id === slideOver.editing!.id
                                ? mapApiToWorkCenter(res.data!)
                                : wc,
                        ),
                    );
                    toast.success('Work center updated successfully');
                }
            } else {
                const res = await createWorkCenter({
                    code: form.code.trim().toUpperCase(),
                    name: form.name,
                    type: form.type,
                    warehouseId: form.warehouseId || null,
                    description: form.description,
                    isActive: form.isActive,
                    costCenterId: form.costCenterId || null,
                    workingHoursPerDay: Number(form.workingHoursPerDay),
                    efficiencyPct: Number(form.efficiencyPct),
                    shiftIds: form.shiftIds,
                    // A type without machines keeps none
                    machineIds: typeUsesMachines(form.type) ? form.machineIds : [],
                    // Persons (manual) or operators per machine, and their standard rate
                    personsRequired: Number(form.personsRequired) || null,
                    stdLabourRate: form.stdLabourRate.trim() === '' ? null : Number(form.stdLabourRate),
                    operatorWageBasis: form.operatorWageBasis,
                });
                if (res.success && res.data) {
                    setWorkCenters((prev) => [mapApiToWorkCenter(res.data!), ...prev]);
                    toast.success('Work center created successfully');
                }
            }
            // Routing, schedule and work order screens pick up the new code / name
            invalidateWorkCenters();
            // Machines may have moved between work centers: reload the list
            getAllWorkCenters()
                .then((r) => { if (r.success && r.data) setWorkCenters(r.data.map(mapApiToWorkCenter)); })
                .catch(() => {});
            closeSlideOver();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Failed to save work center'));
            throw err;
        }
    };

    const handleToggleActive = async (wc: WorkCenter) => {
        // Optimistic update
        setWorkCenters((prev) => prev.map((w) => w.id === wc.id ? { ...w, isActive: !w.isActive } : w));
        try {
            await updateWorkCenter(wc.id, { isActive: !wc.isActive });
            toast.success(`${wc.name} ${wc.isActive ? 'deactivated' : 'activated'}`);
        } catch (err) {
            // Rollback
            setWorkCenters((prev) => prev.map((w) => w.id === wc.id ? { ...w, isActive: wc.isActive } : w));
            toast.error(apiErrorMessage(err, 'Failed to update status'));
        }
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await deleteWorkCenter(deleteConfirm.id);
            invalidateWorkCenters();
            setWorkCenters((prev) => prev.filter((w) => w.id !== deleteConfirm.id));
            toast.success(`"${deleteConfirm.name}" removed`);
            setDeleteConfirm(null);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Failed to delete work center'));
        }
    };

    // Type counts
    const typeCounts = useMemo(() => {
        return allTypeOptions.map((t) => ({
            ...t,
            count: warehouseWorkCenters.filter((w) => w.type === t.value).length,
            badge: getTypeBadge(t.value),
        }));
    }, [warehouseWorkCenters, allTypeOptions]);

    return (
        <AppLayout>
            <div className="p-6 space-y-5 w-full">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate(-1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-[#64748b] transition-colors cursor-pointer">
                            <i className="ri-arrow-left-line text-base" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-bold text-[#1e293b]">Work Centers</h2>
                                <div className="relative group inline-flex items-center">
                                    <button
                                        type="button"
                                        className="w-5 h-5 rounded-full flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors cursor-help"
                                        aria-label="What is a Work Center?"
                                    >
                                        <i className="ri-information-line text-base" />
                                    </button>
                                    <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-50 w-[290px] p-3.5 bg-white text-slate-700 rounded-xl shadow-xl border border-slate-200 text-xs leading-relaxed pointer-events-none transition-all">
                                        <div className="font-semibold text-slate-900 flex items-center gap-1.5 pb-2 border-b border-slate-100">
                                            <i className="ri-building-4-line text-[#4f46e5]" />
                                            <span>What is a Work Center?</span>
                                        </div>
                                        <p className="text-slate-600 text-[11px] leading-relaxed my-2">
                                            A designated production area on the shop floor where manufacturing tasks occur and hourly capacity is tracked.
                                        </p>
                                        <div className="space-y-1.5 text-[11px] pt-1.5 border-t border-slate-100 text-slate-600">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-semibold text-blue-700 w-16 shrink-0">Machine:</span>
                                                <span>Machinery operations only</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-semibold text-emerald-700 w-16 shrink-0">Labor:</span>
                                                <span>Manual assembly &amp; work</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-semibold text-purple-700 w-16 shrink-0">Both:</span>
                                                <span>Combined machine &amp; labor</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-[#64748b] mt-0.5">Manage production work centers and their capacity</p>
                        </div>
                    </div>
                    <button onClick={openAdd}
                        className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-add-line" /> Add Work Center
                    </button>
                </div>

                {/* Filters */}
                <MasterFilters
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Search by code or name..."
                    filters={[
                        {
                            value: typeFilter,
                            onChange: (val) => setTypeFilter(val),
                            options: [
                                { value: 'ALL', label: 'All Types' },
                                ...allTypeOptions.map((t) => ({
                                    value: t.value,
                                    label: getTypeBadge(t.value).label,
                                })),
                            ],
                        },
                        {
                            value: statusFilter,
                            onChange: (val) => setStatusFilter(val),
                            options: [
                                { value: 'ALL', label: 'All Status' },
                                { value: 'ACTIVE', label: 'Active' },
                                { value: 'INACTIVE', label: 'Inactive' },
                            ],
                        },
                    ]}
                    hasActiveFilters={!!(search || typeFilter !== 'ALL' || statusFilter !== 'ALL')}
                    onClearFilters={() => {
                        setSearch('');
                        setTypeFilter('ALL');
                        setStatusFilter('ALL');
                    }}
                />

                {/* Summary cards */}
                <MasterSummaryCards
                    items={typeCounts.map((t) => ({
                        value: t.value,
                        count: t.count,
                        label: t.badge.label,
                        badgeClass: t.badge.cls,
                    }))}
                    activeFilterValue={typeFilter}
                    onFilterChange={(val) => setTypeFilter(val)}
                />

                {/* Stats row */}
                <MasterStatsRow
                    stats={[
                        { label: 'Total Work Centers', value: warehouseWorkCenters.length, icon: 'ri-building-2-line', bg: 'bg-indigo-50', color: 'text-[#4f46e5]' },
                        { label: 'Active', value: warehouseWorkCenters.filter((w) => w.isActive).length, icon: 'ri-checkbox-circle-line', bg: 'bg-green-50', color: 'text-green-600' },
                        { label: 'Avg Plannable h/day', value: fmtHours(warehouseWorkCenters.reduce((a, w) => a + w.workingHoursPerDay * w.efficiencyPct / 100, 0) / (warehouseWorkCenters.length || 1)), icon: 'ri-speed-line', bg: 'bg-blue-50', color: 'text-blue-600' },
                    ]}
                />

                {/* Table */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
                    {/* Scrolls sideways when the columns are wider than the screen */}
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-max text-sm">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                {['Code', 'Work Center Name', 'Type', 'Cost Center', 'Capacity', 'Std Cost / hr', 'Status', ''].map((h) => (
                                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-12 text-center">
                                        <i className="ri-loader-4-line text-4xl text-[#4f46e5] animate-spin block mb-2" />
                                        <p className="text-[#94a3b8] text-sm">Loading work centers...</p>
                                    </td>
                                </tr>
                            ) : filtered.map((wc, idx) => {
                                const badge = getTypeBadge(wc.type);
                                return (
                                    <tr key={wc.id} className={`border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors ${idx % 2 !== 0 ? 'bg-[#f8fafc]/40' : ''}`}>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="font-mono text-xs bg-[#f1f5f9] px-2 py-1 rounded font-semibold text-[#1e293b]">{wc.code || '—'}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2.5">
                                                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${badge.cls} border`}>
                                                    <i className="ri-building-2-line text-sm" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-[#1e293b] whitespace-nowrap">{wc.name}</p>
                                                    {wc.description && <p className="text-xs text-[#94a3b8] truncate max-w-[200px]">{wc.description}</p>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${badge.cls}`}>
                                                {badge.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[#64748b] whitespace-nowrap">
                                            {wc.costCenterName || '—'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <p className="font-semibold text-[#1e293b]" title="Plannable hours per day">
                                                {fmtHours(wc.workingHoursPerDay * wc.efficiencyPct / 100)} <span className="text-xs font-normal text-[#94a3b8]">h/day</span>
                                            </p>
                                            <p className="text-xs text-[#94a3b8]" title="Working hours per day × efficiency">
                                                {wc.workingHoursPerDay} h/day × {wc.efficiencyPct}%
                                                {wc.shiftNames.length > 0 && ` · ${wc.shiftNames.join(', ')}`}
                                            </p>
                                            {wc.machineNames.length > 0 && (
                                                <p className="text-xs text-[#94a3b8]" title={wc.machineNames.join(', ')}>
                                                    <i className="ri-settings-3-line" /> {wc.machineNames.length} machine{wc.machineNames.length > 1 ? 's' : ''}: {wc.machineNames.slice(0, 3).join(', ')}{wc.machineNames.length > 3 ? '…' : ''}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {wc.personsRequired == null && wc.stdCost.total === 0 ? (
                                                <span className="text-xs text-[#94a3b8]" title="Open the work center and enter its people and labour rate">Not set</span>
                                            ) : (
                                                <>
                                                    <p
                                                        className="font-semibold text-[#1e293b]"
                                                        title={`Labour ₹${fmtHours(wc.stdCost.labour)} + operators ₹${fmtHours(wc.stdCost.operators)} + machines ₹${fmtHours(wc.stdCost.machines)}`}
                                                    >
                                                        ₹{fmtHours(wc.stdCost.total)} <span className="text-xs font-normal text-[#94a3b8]">/hr</span>
                                                    </p>
                                                    {wc.personsRequired != null && (
                                                        String(wc.type).toUpperCase() === 'BOTH' ? (
                                                            <p className="text-xs text-[#94a3b8]" title="Labour persons × standard rate, plus each machine's operators and hour rate">
                                                                <i className="ri-team-line" /> {wc.personsRequired} × ₹{fmtHours(wc.stdLabourRate ?? 0)} + operators + machine rate
                                                            </p>
                                                        ) : typeUsesMachines(wc.type) ? (
                                                            <p className="text-xs text-[#94a3b8]" title="Operators per machine × standard labour rate, plus the machine's hour rate">
                                                                <i className="ri-team-line" /> {wc.personsRequired} op/machine × ₹{fmtHours(wc.stdLabourRate ?? 0)} + machine rate
                                                            </p>
                                                        ) : (
                                                            <p className="text-xs text-[#94a3b8]" title="Persons required × standard labour rate">
                                                                <i className="ri-team-line" /> {wc.personsRequired} × ₹{fmtHours(wc.stdLabourRate ?? 0)} = ₹{fmtHours(wc.personsRequired * (wc.stdLabourRate ?? 0))}/hr
                                                            </p>
                                                        )
                                                    )}
                                                    <p className="text-[11px] text-[#94a3b8]">
                                                        {[
                                                            wc.stdCost.labour > 0 && `Labour ₹${fmtHours(wc.stdCost.labour)}`,
                                                            wc.stdCost.operators > 0 && `Operators ₹${fmtHours(wc.stdCost.operators)} (${wc.operatorWageBasis === 'AVG' ? 'avg' : 'total'})`,
                                                            wc.stdCost.machines > 0 && `Machines ₹${fmtHours(wc.stdCost.machines)}`,
                                                        ].filter(Boolean).join(' · ') || '—'}
                                                    </p>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <button onClick={() => handleToggleActive(wc)} className="cursor-pointer" title="Click to toggle status">
                                                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${wc.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${wc.isActive ? 'bg-green-500' : 'bg-slate-400'}`} />
                                                    {wc.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => openEdit(wc)}
                                                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer" title="Edit">
                                                    <i className="ri-edit-line text-sm" />
                                                </button>
                                                <button onClick={() => setDeleteConfirm(wc)}
                                                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer" title="Delete">
                                                    <i className="ri-delete-bin-line text-sm" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!isLoading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-4 py-12 text-center">
                                        <i className="ri-building-2-line text-4xl text-[#e2e8f0] block mb-2" />
                                        <p className="text-[#94a3b8] text-sm">No work centers found</p>
                                        {(search || typeFilter !== 'ALL' || statusFilter !== 'ALL') && (
                                             <p className="text-xs text-[#94a3b8] mt-1">Try adjusting your filters</p>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>

            <WorkCenterSlideOver
                open={slideOver.open}
                editing={slideOver.editing}
                onClose={closeSlideOver}
                onSave={handleSave}
                warehouses={warehouses}
                defaultWarehouseId={selectedWarehouseId}
                typeOptions={allTypeOptions}
                costCenters={costCenters}
            />

            <ConfirmDialog
                open={!!deleteConfirm}
                title="Remove Work Center"
                message={`Remove "${deleteConfirm?.name}"? This action cannot be undone.`}
                variant="danger"
                confirmLabel="Yes, Remove"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteConfirm(null)}
            />
        </AppLayout>
    );
}